/**
 * Light/dark, following the OS, with an in-session override.
 *
 * **Nothing is persisted, and that is the design** (owner ruling, 2026-08-07).
 * The argument: someone who wants dark mode has already said so, to their
 * operating system, and a site that stores its own copy of that answer is
 * keeping a worse duplicate of a preference the platform already holds. On a
 * tool people arrive at through a shared link and use for a few minutes, the OS
 * setting is right essentially always.
 *
 * It also removes a real failure mode rather than a hypothetical one. A stored
 * choice silently outranks the OS forever, so a preference set once — by a
 * visitor, or by someone clicking around while testing — makes the page look
 * broken later with no visible cause and no way to reason about it. A reload
 * does not clear it; that is what storage is for. We hit exactly this an hour
 * after building it.
 *
 * The toggle still exists, in the owner's words, because "you have the system
 * settings for a reason, then you toggle just in case there is hard to read
 * something, but with shift reload it's reset". That is the whole contract: the
 * OS is the standing answer, the button is a momentary one, and a reload always
 * returns to the standing answer. Long enough to read a chart in the other
 * palette or take a screenshot; never long enough to become a stale setting
 * nobody remembers making.
 *
 * **This is why there is no pre-paint inline script.** One used to apply the
 * stored choice before the first frame, because a module import resolves too
 * late and the page would flash the wrong palette. With nothing stored there is
 * nothing to apply: no `data-theme` on load means the `@media
 * (prefers-color-scheme: dark)` block in tokens.css decides, natively, before
 * any script runs. Deleting the storage deleted the flash, the script, its CSP
 * hash, and the duplicated apply-logic that had to be kept in sync by hand.
 *
 * The CSS keeps all three states — bare `:root` light, the media query, and the
 * `[data-theme]` override — because the override is what this toggle stamps.
 */

/** What the visitor can switch to. Absent means "whatever the OS says". */
export type ThemeChoice = "light" | "dark";

/** Labels name what a click will do, not what the theme currently is. */
const LABEL: Record<ThemeChoice, string> = {
  light: "Switch to light theme",
  dark: "Switch to dark theme",
};

const ICON: Record<ThemeChoice, string> = {
  light: `<svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"><circle cx="8" cy="8" r="3.2"/><path d="M8 1v1.6M8 13.4V15M1 8h1.6M13.4 8H15M3.1 3.1l1.1 1.1M11.8 11.8l1.1 1.1M12.9 3.1l-1.1 1.1M4.2 11.8l-1.1 1.1"/></svg>`,
  dark: `<svg viewBox="0 0 16 16" width="15" height="15" aria-hidden="true" fill="currentColor"><path d="M13.3 10.2A5.6 5.6 0 0 1 6 2.9a5.8 5.8 0 1 0 7.3 7.3z"/></svg>`,
};

/** What the OS asks for. The default, and the state on every fresh load. */
export function systemTheme(): ThemeChoice {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

/** What is on screen: the in-session override if one was made, else the OS. */
export function resolvedTheme(): ThemeChoice {
  const attr = document.documentElement.getAttribute("data-theme");
  return attr === "light" || attr === "dark" ? attr : systemTheme();
}

/**
 * Apply an override, or clear one. `undefined` removes the attribute and hands
 * control back to the media query — the state of every fresh page load.
 */
export function applyTheme(choice: ThemeChoice | undefined): void {
  const root = document.documentElement;
  if (choice === undefined) {
    root.removeAttribute("data-theme");
  } else {
    root.setAttribute("data-theme", choice);
  }

  // Keep the browser chrome (mobile address bar, PWA status bar) in step with
  // what is actually on screen.
  //
  // `:not([media])` matters. The app has one unconditional tag for this to
  // rewrite; `glossary.html` has a *pair* scoped by `prefers-color-scheme`, so
  // that a page with no script still colours the chrome correctly. Rewriting
  // the first match there would stamp the dark value onto the tag whose media
  // query says light, and the browser would believe it. Matching only the
  // unconditional tag means this is a no-op on the page that does not need it.
  document
    .querySelector('meta[name="theme-color"]:not([media])')
    ?.setAttribute("content", resolvedTheme() === "dark" ? "#0d0f14" : "#f7f8fa");
}

/**
 * Wire the button as a light/dark toggle for the life of this page.
 *
 * The button ships `hidden` from `ui/chrome.ts` and this reveals it, so it is
 * visible exactly when it is wired. That is not ceremony: `/glossary` is a
 * static document that a reader may well receive with no script running, and a
 * theme button that does nothing when pressed is worse than no button at all.
 * On the app the distinction is invisible, since nothing renders without a
 * script there — but one rule for both pages is what stops the two from
 * drifting again.
 */
export function mountThemeToggle(button: HTMLButtonElement): void {
  let overridden = false;
  button.hidden = false;

  const render = () => {
    // The icon and label show the destination: the button offers a theme rather
    // than reporting one.
    const next: ThemeChoice = resolvedTheme() === "dark" ? "light" : "dark";
    button.innerHTML = ICON[next];
    button.setAttribute("aria-label", LABEL[next]);
    button.title = LABEL[next];
  };

  button.addEventListener("click", () => {
    overridden = true;
    applyTheme(resolvedTheme() === "dark" ? "light" : "dark");
    render();
  });

  // Until the visitor overrides it the page is following the OS, so a change
  // there has to be picked up mid-session: the palette swaps via CSS on its own,
  // but the theme-color meta tag and this button's label both need the nudge.
  window.matchMedia("(prefers-color-scheme: dark)").addEventListener("change", () => {
    if (!overridden) {
      applyTheme(undefined);
      render();
    }
  });

  applyTheme(undefined);
  render();
}
